import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import axios from 'axios';

import { useDebounceState } from 'features/common/hooks/useDebounceState';
import { IPassengerAutocomplete } from 'features/salesOrder/types';

import { getPassengersAutocomplete } from '../../services';
import { PassengersListVariant, PersonTypeTab } from './constants';

const MIN_SEARCH_LENGTH = 4;

interface PassengersAdvancedSearchProps {
	variant: PassengersListVariant;
	personTypeTab: PersonTypeTab;
	selectedPassenger: IPassengerAutocomplete | null;
	onSelect: (passenger: IPassengerAutocomplete) => void;
	onClear: () => void;
}

const formatDocumentLabel = (documentType: string): string => {
	switch (documentType) {
		case 'Passport':
			return 'Pasaporte';
		case 'DriverLicense':
			return 'Libreta de conducir';
		case 'Cedula':
			return 'Cédula';
		default:
			return documentType;
	}
};

export const formatPassengerAutocompleteLabel = (option: IPassengerAutocomplete): string => {
	const base = [`${option.name} ${option.lastName}`.trim(), option.email].filter(Boolean).join(' · ');
	const documents = (option.documents || [])
		.map((doc) => `${formatDocumentLabel(doc.documentType)}: ${doc.documentNumber}`)
		.join(', ');

	return documents ? `${base} · ${documents}` : base;
};

const matchesPersonTypeContext = (
	passenger: IPassengerAutocomplete,
	variant: PassengersListVariant,
	personTypeTab: PersonTypeTab,
): boolean => {
	if (variant === 'passenger') return passenger.isPhysicalPerson;
	if (personTypeTab === 'physical') return passenger.isPhysicalPerson;
	if (personTypeTab === 'legal') return !passenger.isPhysicalPerson;
	return true;
};

export const PassengersAdvancedSearch = ({
	variant,
	personTypeTab,
	selectedPassenger,
	onSelect,
	onClear,
}: PassengersAdvancedSearchProps): JSX.Element => {
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState<IPassengerAutocomplete[]>([]);
	const [loading, setLoading] = useState(false);
	const debouncedInput = useDebounceState(inputValue, 300);

	const personTypeFilter = useMemo(
		() => (passenger: IPassengerAutocomplete) => matchesPersonTypeContext(passenger, variant, personTypeTab),
		[variant, personTypeTab],
	);

	useEffect(() => {
		if (selectedPassenger) {
			setOptions([]);
			return;
		}

		const term = debouncedInput.trim();
		if (term.length < MIN_SEARCH_LENGTH) {
			setOptions([]);
			setLoading(false);
			return;
		}

		const abortController = new AbortController();

		const searchPassengers = async () => {
			setLoading(true);
			try {
				const { data } = await getPassengersAutocomplete(term, abortController.signal);
				if (abortController.signal.aborted) return;
				setOptions(data.filter(personTypeFilter));
			} catch (error) {
				if (abortController.signal.aborted || axios.isCancel(error)) return;
				setOptions([]);
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false);
				}
			}
		};

		searchPassengers();

		return () => {
			abortController.abort();
		};
	}, [debouncedInput, personTypeFilter, selectedPassenger]);

	useEffect(() => {
		if (selectedPassenger) {
			setInputValue(formatPassengerAutocompleteLabel(selectedPassenger));
		}
	}, [selectedPassenger]);

	return (
		<Box sx={{ mt: 2 }}>
			<Autocomplete
				fullWidth
				size="small"
				value={selectedPassenger}
				inputValue={inputValue}
				options={options}
				loading={loading}
				filterOptions={(items) => items}
				isOptionEqualToValue={(option, value) => option.passengerId === value.passengerId}
				getOptionLabel={formatPassengerAutocompleteLabel}
				noOptionsText={
					debouncedInput.trim().length < MIN_SEARCH_LENGTH
						? `Ingresá al menos ${MIN_SEARCH_LENGTH} caracteres`
						: 'Sin resultados'
				}
				onChange={(_, value) => {
					if (value) {
						onSelect(value);
						setInputValue(formatPassengerAutocompleteLabel(value));
						return;
					}
					onClear();
					setInputValue('');
				}}
				onInputChange={(_, value, reason) => {
					if (reason === 'clear') {
						onClear();
						setInputValue('');
						return;
					}
					if (selectedPassenger) {
						onClear();
					}
					setInputValue(value);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Búsqueda avanzada"
						placeholder="Mail, teléfono, nombre, apellido, documento de facturación o documentos de viaje"
						helperText="Usá este buscador para encontrar un pasajero y filtrar la tabla por su ID"
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? <CircularProgress color="inherit" size={18} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>
		</Box>
	);
};
